import React, { useRef, useState } from "react";
import ServiceConfigViewTable from "./serviceConfigView";
import "../util/tables.css";
import { useDataView } from "../../contexts/data.view.context";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import * as API from "../api/index";
import { sliceByEnv, getEnvColumnValue, getEnvRow, getRowDiff, mergeFields } from "../shared/envUtils";
import { NotificationManager } from "react-notifications";
import Tooltip from "@mui/material/Tooltip";
import EditIcon from "@mui/icons-material/Edit";
import AppDialog from "../util/appDialog";
import ViewEditServiceConfig from "./viewEditServiceConfig";
import ShowLoadingBar from "../util/showLoadingBar";
import ConfirmPromoteDialog from "../util/confirmPromoteDialog";
import SyncDialog from "../util/confirmMatchDialog";
import DiffIndicatorIcon from "../util/diffIndicatorIcon";
import ViewDetailsModal from "../util/detailsModal/viewDetailsModal";
import { useFetchOnMount } from "../../hooks/useFetchOnMount";

const ServiceConfigs = () => {
  const {
    serviceConfigs,
    fetchServiceConfigsFromAllEnv,
    REACT_APP_ENV,
    envColumn1,
    envColumn2,
    envCell,
    openPromoteModal,
    setOpenPromoteModal,
    handleClickOpen,
    handleClose,
    openMatchModal,
    setOpenMatchModal,
    matchCell,
    matchSelectedFields,
    setMatchSelectedFields,
    matchDirection,
    setMatchDirection,
    handleMatchClickOpen,
    handleMatchClose,
  } = useDataView();

  const [openDetail, setOpenDetail] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const currentColumnRef = useRef(null);
  const titleRef = useRef(null);

  let emailList = [];

  const handleDetailOpen = ({ cell }) => {
    currentColumnRef.current = cell.getValue();
    titleRef.current = cell.column.id;
    setOpenDetail(true);
  };

  const handleEditOpen = ({ cellValue }) => {
    currentColumnRef.current = cellValue;

    setOpenEdit(true);
  };

  const handleEditClose = () => {
    setOpenEdit(false);
  };

  const handleDetailClose = () => {
    setOpenDetail(false);
  };

  const handleConfirm = () => {
    const payload = [
      {
        ServiceName: envCell.current.row.original.ServiceName,
        PropertyName: envCell.current.row.original.PropertyName,
        PropertyValue: envCell.current.row.original.PropertyValue,
      },
    ];

    const env = envCell.current.column.id;

    onSubmit(payload, env);
    setOpenPromoteModal(false);
  };

  const handleConfirmOnMatch = (fields, direction) => {
    const current = matchCell.current && matchCell.current.cell.row.original;
    const target = matchCell.current && matchCell.current.targetRow;

    if (!current || !target) {
      setOpenMatchModal(false);
      return;
    }

    // _ServiceName/_PropertyName (leading underscore) are required by the
    // backend upsert to take the UPDATE branch instead of silently
    // INSERTing a duplicate — see mergeFields below: they're never in
    // `fields`, so they always come from whichever row (current/target)
    // is actually being written to.
    const fieldNames = ["ServiceName", "PropertyName", "PropertyValue", "_ServiceName", "_PropertyName"];
    const isPush = direction === "push";
    const merged = isPush
      ? mergeFields(fieldNames, target, current, fields)
      : mergeFields(fieldNames, current, target, fields);
    const destEnv = isPush ? matchCell.current.cell.column.id : REACT_APP_ENV;

    onSubmit([merged], destEnv);
    setOpenMatchModal(false);
  };

  const onSubmit = (payload, env) => {
    const resourcePath = "integrations/cfg/serviceconfigs";

    API.AddRecordToOtherEnv(resourcePath, env, payload).then((response) => {
      if (response.code === 200) {
        NotificationManager.success(
          "Record added to " + env + " env",
          "Service Config",
          10000
        );
        fetchServiceConfigsFromAllEnv();
      } else {
        NotificationManager.error(response.message, "Failed!", 10000);
      }
    });
  };

  let currentEnvServiceConfigs = [];
  let envSlice = { current: [], dev: [], test: [], prod: [] };

  const mainColumns = [
    {
      accessorKey: "ServiceName",
      header: "Service Name",
      size: 75,
      enableEditing: false,
      Cell: editRecord(),
    },
    {
      accessorKey: "PropertyName",
      header: "Property Name",
      size: 75,
      enableEditing: false,
    },
    {
      accessorKey: "PropertyValue",
      header: "Property Value",
      size: 150,
      Cell: viewDetails(),
    },
    {
      accessorKey: "envColumn1",
      header: envColumn1,
      id: envColumn1,
      size: 150,
      Cell: customCellAction(),
      enableEditing: false,
    },
    {
      accessorKey: "envColumn2", //access nested data with dot notation
      header: envColumn2,
      id: envColumn2,
      size: 150,
      Cell: customCellAction(),
      enableEditing: false,
    },
  ];

  function customCellAction() {
    return function ({ cell }) {
      const cellValue = cell.getValue();

      if (cellValue === "N/A") {
        return (
          <ConfirmPromoteDialog
            cell={cell}
            open={openPromoteModal}
            envCell={envCell}
            handleClickOpen={handleClickOpen}
            handleClose={handleClose}
            handleConfirm={handleConfirm}
          />
        );
      }

      const diffInfo = cell.row.original[`${cell.column.id}__diff`] || {
        isDifferent: false,
        diffFields: [],
        targetRow: null,
      };

      const displayValue =
        cellValue.length > 49 ? (
          <>
            {cellValue.substring(0, 49)}
            {"…"}
            <ViewDetailsModal
              cell={cell}
              openDetail={openDetail}
              currentColumnRef={currentColumnRef}
              handleDetailOpen={handleDetailOpen}
              handleDetailClose={handleDetailClose}
              title={titleRef.current}
            />
          </>
        ) : (
          cellValue
        );

      return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {displayValue}
          <DiffIndicatorIcon
            cell={cell}
            isDifferent={diffInfo.isDifferent}
            diffFields={diffInfo.diffFields}
            targetRow={diffInfo.targetRow}
            handleMatchClickOpen={handleMatchClickOpen}
          />
        </Box>
      );
    };
  }

  function viewDetails() {
    return function ({ cell }) {
      const cellValue = cell.getValue();

      if (cellValue.length > 50) {
        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {cellValue.substring(0, 49)}
            {"…"}
            <ViewDetailsModal
              cell={cell}
              openDetail={openDetail}
              currentColumnRef={currentColumnRef}
              handleDetailOpen={handleDetailOpen}
              handleDetailClose={handleDetailClose}
              title={titleRef.current}
            />
          </Box>
        );
      }

      return cellValue;
    };
  }

  function editRecord() {
    return function ({ cell, row }) {
      const cellText = cell.getValue();
      const cellValue = row;
      const tooltip = "Edit Record";

      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
            {cellText}
          </Box>
          <IconButton
            color="primary"
            size="small"
            sx={{ "&:hover": { bgcolor: "action.hover" } }}
            onClick={() => handleEditOpen({ cellValue })}
          >
            <Tooltip id="button-report" title={tooltip}>
              <EditIcon fontSize="small" />
            </Tooltip>
          </IconButton>

          <AppDialog
            open={openEdit}
            onClose={handleEditClose}
            title="Edit Service Config"
            maxWidth="sm"
          >
            <ViewEditServiceConfig
              curRow={currentColumnRef.current}
              handleEditClose={handleEditClose}
              emailList={emailList}
            />
          </AppDialog>
        </Box>
      );
    };
  }

  useFetchOnMount(serviceConfigs, fetchServiceConfigsFromAllEnv);

  if (serviceConfigs.length > 0) {
    envSlice = sliceByEnv(serviceConfigs, REACT_APP_ENV);
    currentEnvServiceConfigs = envSlice.current;
  }

  let loading = false;
  if (currentEnvServiceConfigs.length === 0) {
    loading = true;
  } else {
    emailList = getEmailList(currentEnvServiceConfigs);
  }

  if (Boolean(loading)) {
    return <ShowLoadingBar title="LIST of SERVICE CONFIGS" loading={loading} />;
  }

  return (
    <div className="service-config-table">

      {currentEnvServiceConfigs.length > 0 && (
        <ServiceConfigViewTable
          data={getTableData(
            currentEnvServiceConfigs,
            envColumn1,
            envColumn2,
            envSlice
          )}
          mainColumns={mainColumns}
          emailList={emailList}
        />
      )}

      <SyncDialog
        open={openMatchModal}
        matchCell={matchCell}
        matchSelectedFields={matchSelectedFields}
        setMatchSelectedFields={setMatchSelectedFields}
        matchDirection={matchDirection}
        setMatchDirection={setMatchDirection}
        handleMatchClose={handleMatchClose}
        handleMatchConfirm={handleConfirmOnMatch}
      />
    </div>
  );
};

export default ServiceConfigs;

function getTableData(
  currentEnvServiceConfigs,
  envColumn1,
  envColumn2,
  envSlice
) {
  return currentEnvServiceConfigs.map((serviceConfig) => {
    const { _ServiceName, _PropertyName } = serviceConfig;
    const matcher = (c) => c._ServiceName === _ServiceName && c._PropertyName === _PropertyName;

    const col1 = getEnvColumnValue({ envColumn: envColumn1, ...envSlice, matcher, valueKey: "PropertyValue" });
    const col2 = getEnvColumnValue({ envColumn: envColumn2, ...envSlice, matcher, valueKey: "PropertyValue" });
    const targetRow1 = getEnvRow({ envColumn: envColumn1, ...envSlice, matcher });
    const targetRow2 = getEnvRow({ envColumn: envColumn2, ...envSlice, matcher });

    return {
      _ServiceName,
      _PropertyName,
      ServiceName: serviceConfig.ServiceName,
      PropertyName: serviceConfig.PropertyName,
      PropertyValue: serviceConfig.PropertyValue,
      envColumn1: col1,
      envColumn2: col2,
      [`${envColumn1}__diff`]: { ...getRowDiff(serviceConfig, targetRow1), targetRow: targetRow1 },
      [`${envColumn2}__diff`]: { ...getRowDiff(serviceConfig, targetRow2), targetRow: targetRow2 },
    };
  });
}

function getEmailList(currentEnvServiceConfigs) {
  const duplicateListArr = [];
  const serviceConfigValues = currentEnvServiceConfigs.map((serviceConfig) => {
    return serviceConfig.PropertyValue;
  });

  const tempEmailValues = serviceConfigValues.filter((value) => {
    return value.includes("@");
  });

  tempEmailValues.forEach((val) => {
    const mailArr = val.split(",");
    mailArr.forEach(function (element) {
      duplicateListArr.push(element.toLowerCase());
    });
  });

  const emailList = duplicateListArr.filter((element, index) => {
    return duplicateListArr.indexOf(element) === index;
  });
  return emailList;
}

